/**
 * Store Import Screen
 * 
 * Screen for importing stores from CSV files.
 * Allows managers to upload CSV, review/edit stores, and import to database.
 * 
 * @module StoreImportScreen
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Upload, Download, ArrowLeft, AlertCircle, CheckCircle, X, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { StoreImportService, StoreImportRow, StoreParseResult } from '../../services/StoreImportService';
import { StoreImportTable } from '../../components/manager/StoreImportTable';
import { AccessCodeModal } from '../../components/manager/AccessCodeModal';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../config/firebase';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';

/**
 * Styled Components
 */
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background: white;
  color: ${ThemeColors.text.primary};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${ThemeColors.background.secondary};
    border-color: ${ThemeColors.primary.main};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InstructionsPanel = styled.div<{ isExpanded: boolean }>`
  background: ${ThemeColors.background.cards};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: 8px;
  padding: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const InstructionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const InstructionsTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const InstructionsContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-top: ${props => props.isExpanded ? Spacing.md : '0'};
`;

const InstructionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  margin-bottom: ${Spacing.md};
`;

const InstructionItem = styled.li`
  padding: ${Spacing.xs} 0;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.xs};

  &::before {
    content: '•';
    color: ${ThemeColors.primary.main};
    font-weight: bold;
    margin-right: ${Spacing.xs};
  }
`;

const RequiredColumns = styled.div`
  margin-top: ${Spacing.md};
`;

const ColumnList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.sm};
`;

const ColumnTag = styled.span<{ required?: boolean }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${props => props.required ? ThemeColors.error.light : ThemeColors.background.secondary};
  color: ${props => props.required ? ThemeColors.error.dark : ThemeColors.text.primary};
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  border: 1px solid ${props => props.required ? ThemeColors.error.main : ThemeColors.border.light};
`;

const DownloadTemplateButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: ${Spacing.md};

  &:hover {
    background: ${ThemeColors.primary.dark};
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FileUploadArea = styled.div<{ isDragging?: boolean }>`
  border: 2px dashed ${props => props.isDragging ? ThemeColors.primary.main : ThemeColors.border.main};
  border-radius: 8px;
  padding: ${Spacing.xl};
  text-align: center;
  background: ${props => props.isDragging ? ThemeColors.primary.light : ThemeColors.background.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: ${Spacing.lg};

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileName = styled.div`
  margin-top: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  font-weight: ${Typography.fontWeight.medium};
`;

const MessageBanner = styled.div<{ type: 'success' | 'error' | 'warning' }>`
  padding: ${Spacing.md};
  border-radius: 8px;
  margin-bottom: ${Spacing.lg};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  background: ${props => {
    switch (props.type) {
      case 'success': return ThemeColors.success.light;
      case 'error': return ThemeColors.error.light;
      case 'warning': return ThemeColors.warning?.light || '#FFF3CD';
      default: return ThemeColors.background.secondary;
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success': return ThemeColors.success.dark;
      case 'error': return ThemeColors.error.dark;
      case 'warning': return ThemeColors.warning?.dark || '#856404';
      default: return ThemeColors.text.primary;
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  margin-top: ${Spacing.lg};
  margin-bottom: ${Spacing.lg};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.lg};
  border: none;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${ThemeColors.primary.main};
          color: white;
          &:hover:not(:disabled) {
            background: ${ThemeColors.primary.dark};
            transform: translateY(-1px);
          }
        `;
      case 'danger':
        return `
          background: ${ThemeColors.error.main};
          color: white;
          &:hover:not(:disabled) {
            background: ${ThemeColors.error.dark};
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background: white;
          color: ${ThemeColors.text.primary};
          border: 1px solid ${ThemeColors.border.main};
          &:hover:not(:disabled) {
            background: ${ThemeColors.background.secondary};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  color: white;
  font-size: ${Typography.fontSize.lg};
`;

/**
 * Store Import Screen Component
 */
export const StoreImportScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [stores, setStores] = useState<StoreImportRow[]>([]);
  const [editingId, setEditingId] = useState<string | undefined>();
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [instructionsExpanded, setInstructionsExpanded] = useState(true);

  /**
   * Handle file drop
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback(async (file: File) => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
      setMessage({ type: 'error', text: 'Please upload a CSV or XLSX file' });
      return;
    }

    setSelectedFile(file);
    setMessage(null);
    setIsParsing(true);

    try {
      const result: StoreParseResult = await StoreImportService.parseStoreFile(file);
      
      setStores(result.stores);
      
      // Validate stores and show alert if there are errors
      const invalidStores = result.stores.filter(s => !s.isValid);
      if (invalidStores.length > 0) {
        const errorDetails = invalidStores.map(s => {
          const rowNum = result.stores.indexOf(s) + 1;
          const missingFields = [];
          if (!s.CustomerNo?.trim()) missingFields.push('CustomerNo');
          if (!s.StoreName?.trim()) missingFields.push('StoreName');
          if (!s.Address?.trim()) missingFields.push('Address');
          if (!s.City?.trim()) missingFields.push('City');
          return `Row ${rowNum}: Missing ${missingFields.join(', ')}`;
        }).join('\n');

        setMessage({
          type: 'warning',
          text: `${invalidStores.length} store(s) have missing required fields. Please review and fix them before importing.\n\n${errorDetails}`
        });
      } else {
        setMessage({
          type: 'success',
          text: `Successfully parsed ${result.stores.length} store(s). Review and click Import Stores to continue.`
        });
      }
    } catch (error: any) {
      console.error('Error parsing CSV:', error);
      setMessage({ type: 'error', text: `Failed to parse CSV file: ${error.message}` });
      setStores([]);
    } finally {
      setIsParsing(false);
    }
  }, []);

  /**
   * Handle store update
   */
  const handleStoreUpdate = useCallback((id: string, field: keyof StoreImportRow, value: string) => {
    setStores(prev => prev.map(store => {
      if (store.id === id) {
        const updated = { ...store, [field]: value };
        // Re-validate
        const errors: string[] = [];
        if (!updated.CustomerNo?.trim()) errors.push('CustomerNo is required');
        if (!updated.StoreName?.trim()) errors.push('StoreName is required');
        if (!updated.Address?.trim()) errors.push('Address is required');
        if (!updated.City?.trim()) errors.push('City is required');
        updated.errors = errors;
        updated.isValid = errors.length === 0;
        return updated;
      }
      return store;
    }));
  }, []);

  /**
   * Handle store remove
   */
  const handleStoreRemove = useCallback((id: string) => {
    setStores(prev => prev.filter(store => store.id !== id));
  }, []);

  /**
   * Handle add store
   */
  const handleAddStore = useCallback(() => {
    const newStore: StoreImportRow = {
      CustomerNo: '',
      StoreName: '',
      Address: '',
      City: '',
      id: `temp-${Date.now()}`,
      errors: ['CustomerNo is required', 'StoreName is required', 'Address is required', 'City is required'],
      isValid: false
    };
    setStores(prev => [...prev, newStore]);
    setEditingId(newStore.id);
  }, []);

  /**
   * Handle edit start
   */
  const handleEditStart = useCallback((id: string) => {
    setEditingId(id);
  }, []);

  /**
   * Handle edit cancel
   */
  const handleEditCancel = useCallback(() => {
    setEditingId(undefined);
  }, []);

  /**
   * Handle edit save
   */
  const handleEditSave = useCallback((id: string) => {
    // Re-validate the store before saving
    setStores(prev => prev.map(store => {
      if (store.id === id) {
        const errors: string[] = [];
        if (!store.CustomerNo?.trim()) errors.push('CustomerNo is required');
        if (!store.StoreName?.trim()) errors.push('StoreName is required');
        if (!store.Address?.trim()) errors.push('Address is required');
        if (!store.City?.trim()) errors.push('City is required');
        return {
          ...store,
          errors,
          isValid: errors.length === 0
        };
      }
      return store;
    }));
    setEditingId(undefined);
  }, []);

  /**
   * Handle import confirmation
   */
  const handleImportConfirm = useCallback(async (accessCode: string) => {
    if (!StoreImportService.validateAccessCode(accessCode)) {
      setMessage({ type: 'error', text: 'Invalid access code. Please try again.' });
      return;
    }

    // Get Firebase Auth UID (not Firestore document ID)
    const firebaseUser = auth.currentUser;
    if (!firebaseUser?.uid) {
      setMessage({ type: 'error', text: 'User not authenticated. Please log in again.' });
      return;
    }

    setShowAccessCodeModal(false);
    setIsImporting(true);
    setMessage(null);

    try {
      const result = await StoreImportService.importStores(stores, firebaseUser.uid);
      
      if (result.success) {
        setMessage({
          type: 'success',
          text: result.message || `Successfully imported ${result.importedCount} store(s)!`
        });
        
        // Redirect to store management after 2 seconds
        setTimeout(() => {
          navigate('/manager/stores');
        }, 2000);
      } else {
        setMessage({
          type: 'error',
          text: result.message || 'Failed to import stores. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('Error importing stores:', error);
      setMessage({ type: 'error', text: `Failed to import stores: ${error.message}` });
    } finally {
      setIsImporting(false);
    }
  }, [stores, user, navigate]);

  /**
   * Handle import button click
   */
  const handleImportClick = useCallback(() => {
    const validStores = stores.filter(s => s.isValid);
    if (validStores.length === 0) {
      setMessage({ type: 'error', text: 'No valid stores to import. Please fix validation errors first.' });
      return;
    }
    setShowAccessCodeModal(true);
  }, [stores]);

  /**
   * Handle clear
   */
  const handleClear = useCallback(() => {
    setSelectedFile(null);
    setStores([]);
    setMessage(null);
    setEditingId(undefined);
  }, []);

  /**
   * Handle download template
   */
  const handleDownloadTemplate = useCallback(() => {
    StoreImportService.downloadCSVTemplate();
  }, []);

  const validStoresCount = stores.filter(s => s.isValid).length;
  const invalidStoresCount = stores.length - validStoresCount;

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Header>
          <Title>
            <FileText size={32} />
            Import Stores
          </Title>
          <BackButton onClick={() => navigate('/manager/stores')}>
            <ArrowLeft />
            Back to Store Management
          </BackButton>
        </Header>

        {/* Instructions Panel */}
        <InstructionsPanel isExpanded={instructionsExpanded}>
          <InstructionsHeader onClick={() => setInstructionsExpanded(!instructionsExpanded)}>
            <InstructionsTitle>
              <FileText size={20} />
              CSV Import Instructions
            </InstructionsTitle>
            {instructionsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </InstructionsHeader>
          <InstructionsContent isExpanded={instructionsExpanded}>
            <InstructionsList>
              <InstructionItem>Supported formats: CSV (comma or semicolon-delimited) or Excel (XLSX/XLS)</InstructionItem>
              <InstructionItem>The first row must contain column headers</InstructionItem>
              <InstructionItem>Required columns must have values for all rows</InstructionItem>
              <InstructionItem>You can review and edit stores before importing</InstructionItem>
              <InstructionItem>Stores will be added to the existing database (no overwriting)</InstructionItem>
            </InstructionsList>
            <RequiredColumns>
              <strong>Required Columns:</strong>
              <ColumnList>
                {['CustomerNo', 'StoreName', 'Address', 'City'].map(col => (
                  <ColumnTag key={col} required>{col}</ColumnTag>
                ))}
              </ColumnList>
            </RequiredColumns>
            <RequiredColumns style={{ marginTop: Spacing.md }}>
              <strong>Optional Columns:</strong>
              <ColumnList>
                {['SalespersonNo', 'SalesGroup', 'Prov', 'PostalCode', 'StoreNumber', 'SortField', 'Telephone'].map(col => (
                  <ColumnTag key={col}>{col}</ColumnTag>
                ))}
              </ColumnList>
            </RequiredColumns>
            <DownloadTemplateButton onClick={handleDownloadTemplate}>
              <Download />
              Download CSV Template
            </DownloadTemplateButton>
          </InstructionsContent>
        </InstructionsPanel>

        {/* Message Banner */}
        {message && (
          <MessageBanner type={message.type}>
            {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <div style={{ whiteSpace: 'pre-line' }}>{message.text}</div>
            <button
              onClick={() => setMessage(null)}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit'
              }}
            >
              <X size={20} />
            </button>
          </MessageBanner>
        )}

        {/* File Upload Area */}
        <FileUploadArea
          isDragging={isDragging}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => document.getElementById('store-file-input')?.click()}
        >
          <Upload size={48} style={{ marginBottom: Spacing.md, color: ThemeColors.primary.main }} />
          <p style={{ fontSize: Typography.fontSize.lg, marginBottom: Spacing.xs }}>
            Drop CSV file here or click to upload
          </p>
          <p style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary }}>
            Supported formats: CSV (.csv) or Excel (.xlsx, .xls)
          </p>
          {selectedFile && (
            <FileName>
              Selected: {selectedFile.name}
            </FileName>
          )}
          <FileInput
            id="store-file-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
        </FileUploadArea>

        {/* Store Table */}
        {stores.length > 0 && (
          <>
            <div style={{ marginBottom: Spacing.md, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <strong>Stores to Import:</strong> {validStoresCount} valid, {invalidStoresCount} with errors
              </div>
            </div>

            {/* Action Buttons */}
            <ActionButtons style={{ marginBottom: Spacing.lg }}>
              <Button
                variant="primary"
                onClick={handleImportClick}
                disabled={validStoresCount === 0 || isImporting}
              >
                <Upload />
                Import Stores ({validStoresCount})
              </Button>
              <Button variant="secondary" onClick={handleClear} disabled={isImporting}>
                Clear
              </Button>
            </ActionButtons>

            <StoreImportTable
              stores={stores}
              onUpdate={handleStoreUpdate}
              onRemove={handleStoreRemove}
              onAdd={handleAddStore}
              editingId={editingId}
              onEditStart={handleEditStart}
              onEditCancel={handleEditCancel}
              onEditSave={handleEditSave}
            />
          </>
        )}

        {/* Access Code Modal */}
        <AccessCodeModal
          isOpen={showAccessCodeModal}
          onConfirm={handleImportConfirm}
          onCancel={() => setShowAccessCodeModal(false)}
          isLoading={isImporting}
        />

        {/* Loading Overlay */}
        {(isParsing || isImporting) && (
          <LoadingOverlay>
            {isParsing ? 'Parsing CSV file...' : 'Importing stores...'}
          </LoadingOverlay>
        )}
      </MainContent>
    </PageContainer>
  );
};

